import { isValid, getFormValues } from 'redux-form';
import { createValidator } from 'utils/formValidations';
import purchaserValidations from 'utils/purchaserValidations';
import {
  getEnabledBillingCompanyDocuments,
  isBillingCompanyDocumentOrLegacy,
} from 'utils/purchase/billingCompanyDocuments';

/**
 * Check whether a form scalar contains a non-empty string.
 * @param {*} value - Candidate form value.
 * @returns {boolean} Whether the value is a non-empty string.
 */
const hasValue = (value) => typeof value === 'string' && value.trim().length > 0;

/**
 * Client-side validity of the purchaser form, independent of redux-form field registration.
 *
 * redux-form's `isValid` only counts errors for currently-registered fields, so it reports the
 * form as valid when it is unmounted (the wallet flow can re-render it away) even though the
 * persisted values are invalid. That let the Google Pay / Apple Pay sheet open with invalid data.
 * To gate reliably we also re-run the form-level validator against the persisted values, which
 * survive unmount (`destroyOnUnmount: false`).
 *
 * The error filter mirrors which fields the form actually renders, since a field that is not
 * shown is never collected and must not be treated as required here (otherwise it would block the
 * wallet for a configuration where it is intentionally absent). Keep this in sync with
 * `PurchaserFields`:
 * - `legalTerms`: handled separately via `requiresTermsValidation`.
 * - `email`: only shown (and required) when `SHOW_PURCHASER_EMAIL_FIELD`.
 * - `purchaserLastName`: not rendered when the tenant collects a business name for a configured
 *   billing document (or legacy NIT); the business name goes into `purchaserFirstName`.
 *
 * Coverage caveat: this only re-runs the FORM-LEVEL validator (`purchaserValidations`). The
 * field-level `validate` props on the purchaser form — `documentId` (PurchaserFields) and the
 * AddressFields (`zipCode`, `streetName`, `streetNumber`, `neighborhood`, `city`, `country`) — are
 * NOT mirrored here. While the form is mounted `isValid('purchaser')` still catches them; the only
 * gap is the unmounted window, and only for the few tenants that render those fields. If a wallet
 * must hard-block on documentId/address validity, add those validators here too.
 *
 * @param {Object} state - Redux state.
 * @returns {boolean} True when the purchaser form values are client-side valid.
 */
export default function selectIsPurchaserFormValid(state) {
  const features = state?.whitelabelConfig?.features || {};
  const values = getFormValues('purchaser')(state) || {};
  const allowedBillingDocuments = getEnabledBillingCompanyDocuments(
    features.BILLING_COMPANY_ENABLED,
    features.BILLING_COMPANY_AVAILABLE_DOCUMENTS,
  );
  const billingCompanyFlowConfigured = allowedBillingDocuments.length > 0;
  const usesBusinessName =
    Boolean(features.ACCEPT_PURCHASER_BUSINESS_NAME) &&
    isBillingCompanyDocumentOrLegacy(values.documentType, allowedBillingDocuments);
  // The enabled billing document types are per-tenant configuration, already resolved by the form
  // into this marker (see BILLING_COMPANY_ENABLED and BILLING_COMPANY_AVAILABLE_DOCUMENTS).
  const validatesBillingCompany =
    billingCompanyFlowConfigured && Boolean(values.billingCompanyValidationEnabled);

  // Third-party invoice flow (ENABLE_THIRD_PARTY_INVOICE): the recipient's NIT is captured in
  // `documentId` and the `taxpayerId` / `taxpayerLegalName` fields are never rendered (see
  // BillingRecipientSection / PurchaserFields). Their form-level validators still fire for an
  // `organization` recipient, so without this exclusion the persisted values keep those errors and
  // `isPurchaserFormValid` stays false forever — permanently blocking the wallet sheets.
  const usesThirdPartyRecipient = Boolean(values.billingRecipientType);

  const errorKeys = Object.keys(createValidator(purchaserValidations)(values)).filter((key) => {
    if (key === 'legalTerms') return false;
    if (key === 'email') return Boolean(features.SHOW_PURCHASER_EMAIL_FIELD);
    if (key === 'purchaserLastName') return !usesBusinessName;
    if (key === 'taxpayerId' || key === 'taxpayerLegalName') return !usesThirdPartyRecipient;
    return true;
  });

  if (validatesBillingCompany) {
    // A resolved company does not require the city selector. Manual creation does.
    const hasValidCompanyData =
      hasValue(values.taxpayerLegalName) &&
      (Boolean(values.billingCompanyId) || hasValue(values.billingCompanyCityId));

    if (!hasValidCompanyData) return false;
  }

  return isValid('purchaser')(state) && errorKeys.length === 0;
}
